import type { PlaceType } from "../data/destinations";

export type WeatherDay = {
  date: string;
  code: number;
  dailyCode?: number;
  daylightHours?: number;
  temperatureMin: number;
  temperatureMax: number;
  precipitation: number;
  precipitationProbability: number;
  snowfall?: number;
  sunshineHours: number;
  windMax: number;
};

export type PlaceForecast = {
  id: string;
  name: string;
  region: string;
  province?: string;
  country?: string;
  matchLabel?: string;
  latitude: number;
  longitude: number;
  elevation?: number;
  category?: PlaceType;
  preferred?: boolean;
  distanceKm?: number;
  timezone?: string;
  days: WeatherDay[];
  updatedAt: string;
};

export type WeatherKind = "clear" | "partly-cloudy" | "cloudy" | "fog" | "rain" | "snow" | "storm";

export function daytimeCode(dailyCode: number, codes: number[]): number {
  if (!codes.length) return dailyCode;
  // Keep hazardous conditions visible even when most daylight hours are sunny.
  const hazards = codes.filter(code => code >= 51);
  if (hazards.length) return Math.max(...hazards);
  const counts = new Map<number, number>();
  codes.forEach(code => counts.set(code, (counts.get(code) ?? 0) + 1));
  return [...counts].sort((a, b) => b[1] - a[1] || b[0] - a[0])[0]?.[0] ?? dailyCode;
}

// Sunshine duration measures direct sunshine, independently of cloud-cover WMO codes.
export function sunshineFraction(day: WeatherDay): number {
  return day.sunshineHours / (day.daylightHours && day.daylightHours > 0 ? day.daylightHours : 12);
}

export function displayWeatherCode(day: WeatherDay): number {
  // Never replace precipitation, thunderstorm or fog warnings with sunshine.
  if (day.code > 3) return day.code;
  if (sunshineFraction(day) >= .8) return 0;
  if (day.sunshineHours >= 2) return 2;
  return day.code;
}

export function isSunnyDay(day?: WeatherDay): boolean {
  return !!day && displayWeatherCode(day) <= 2 && sunshineFraction(day) >= .5 && day.sunshineHours >= 4;
}

export function isGoodWeather(day?: WeatherDay): boolean {
  return isSunnyDay(day) && !!day && day.precipitation <= 1 && day.precipitationProbability <= 30;
}

export function weatherKind(code: number): WeatherKind {
  if (code === 0) return "clear";
  if (code <= 2) return "partly-cloudy";
  if (code === 3) return "cloudy";
  if (code === 45 || code === 48) return "fog";
  if (code >= 95) return "storm";
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return "snow";
  return "rain";
}

export function weatherLabel(code: number): string {
  const kind = weatherKind(code);
  return {
    clear: "Sereno",
    "partly-cloudy": "Poco nuvoloso",
    cloudy: "Coperto",
    fog: "Nebbia",
    rain: "Pioggia",
    snow: "Neve",
    storm: "Temporali"
  }[kind];
}

export function formatDay(date: string, long = false): string {
  return new Intl.DateTimeFormat("it-IT", long
    ? { weekday: "long", day: "numeric", month: "long" }
    : { weekday: "short", day: "numeric" }).format(new Date(`${date}T12:00:00`));
}

export function tripScore(days: WeatherDay[]): number {
  if (!days.length) return 0;
  const total = days.reduce((score, day) => {
    const rainPenalty = Math.min(42, day.precipitation * 4 + day.precipitationProbability * 0.22);
    const comfort = Math.max(0, 24 - Math.abs(19 - (day.temperatureMin + day.temperatureMax) / 2) * 2.2);
    const sun = Math.min(24, day.sunshineHours * 2.5);
    const windPenalty = Math.max(0, day.windMax - 28) * 0.7;
    return score + 52 + comfort + sun - rainPenalty - windPenalty;
  }, 0);
  return Math.round(Math.max(0, Math.min(100, total / days.length)));
}
